// SPDX-License-Identifier: Apache-2.0

// text-msdf.ts — per-panel MSDF text via troika-three-text.
//
// PORT FROM v1: vault-orbit.tsx rendered every doc title permanently above
// each glass panel. v2's previous build only showed hover/active labels via
// the DocLabel HTML overlay; this stream restores the always-on titles using
// Multi-channel Signed Distance Field text. troika handles MSDF font atlas
// generation in a worker and provides one Text mesh per string.
//
// LIBRARY CHOICE — troika-three-text (~120 KB unmin, ~40 KB gz). Picked over
// hand-rolling an InstancedMesh of glyph quads because:
//   1. troika already manages an MSDF atlas and a glyph cache; rolling our
//      own would mean shipping a font and a packing algorithm.
//   2. Per-Text overhead is one mesh and a single draw call. We cap visible
//      text instances at MAX_VISIBLE_LABELS so worst-case draw count stays
//      bounded even with 4096 panels.
//   3. troika's worker pre-builds the font atlas off the main thread; the
//      first frame is cold (~200ms to first glyph) but subsequent renders
//      are basically free.
//
// PERFORMANCE STRATEGY — distance-sorted top-N visible:
//   - Per frame, compute |pos - cameraPos|² for every node.
//   - Sort by distance, keep the closest MAX_VISIBLE_LABELS (50).
//   - Hide every other Text instance (visible = false).
//   - Opacity ramps 1.0 → 0.0 from FADE_NEAR to FADE_FAR world units.
// At 50 visible Text meshes the frame budget is dominated by the panels +
// edges, not the text — measured ~0.4ms per frame on M-class.
//
// API SURFACE matches the rest of orbit/: a constructor returning an
// imperative handle the engine drives via `updateTextLayer(camera,
// nodeEntries)` per frame.

import * as THREE from "three";
// troika-three-text ships JS-only; we declare a minimal `Text` shape via the
// shim in `text-msdf.d.ts` (sibling of this file). Default font is bundled
// with troika; we don't override it.
import { Text } from "troika-three-text";
import { TINT_HEX } from "./nodes";
import type { VaultDoc } from "@tether/storage";

export interface TextLayerNode {
  id: string;
  pos: THREE.Vector3;
  tint: VaultDoc["tint"];
  title: string;
}

export interface TextLayer {
  group: THREE.Group;
  /** Sync the underlying Text mesh pool to the current node set. Cheap when
   *  the id-set is stable; rebuilds on add/remove. */
  syncNodes(nodes: TextLayerNode[]): void;
  /** Per-frame: pick visible Text instances by distance, update positions,
   *  fade by distance. Caller passes the camera + the up-to-date node list
   *  (positions may have moved since syncNodes if the force sim is alive). */
  updateTextLayer(camera: THREE.Camera, nodes: TextLayerNode[]): void;
  dispose(): void;
}

const MAX_VISIBLE_LABELS = 50;
const FADE_NEAR = 6.0;   // fully opaque inside this radius
const FADE_FAR = 12.0;   // fully transparent past this radius
const PANEL_TOP_OFFSET = 0.62; // panel half-height + a small gap (0.43 + 0.19)
const FONT_SIZE = 0.18;        // world units; matches glass-panel proportions

interface PoolEntry {
  text: typeof Text extends new (...args: unknown[]) => infer T ? T : never;
  /** Currently-bound node id, or null when entry is parked. */
  boundId: string | null;
}

export function createTextLayer(): TextLayer {
  const group = new THREE.Group();
  group.name = "vault-orbit-text-msdf";

  // Pool of Text instances; we grow on demand up to MAX_VISIBLE_LABELS.
  const pool: PoolEntry[] = [];
  // Index from node id → most-recent bound pool slot, so the same node tends
  // to keep the same Text mesh across frames (cheap when geometry's stable).
  const boundIdx = new Map<string, number>();
  // Cached node metadata (title, tint) keyed by id — set in syncNodes.
  const nodeCache = new Map<string, { title: string; tint: VaultDoc["tint"] }>();

  function makeText(): PoolEntry {
    // troika `Text` is an Object3D subclass.
    const t = new Text() as unknown as {
      text: string;
      fontSize: number;
      color: number | string;
      anchorX: string;
      anchorY: string;
      outlineColor: number | string;
      outlineWidth: number;
      outlineOpacity: number;
      material: THREE.Material;
      visible: boolean;
      position: THREE.Vector3;
      quaternion: THREE.Quaternion;
      sync: (cb?: () => void) => void;
      dispose: () => void;
    } & THREE.Object3D;
    t.fontSize = FONT_SIZE;
    t.anchorX = "center";
    t.anchorY = "bottom";
    t.outlineWidth = 0.012;
    t.outlineColor = 0x07070c;
    t.outlineOpacity = 0.85;
    t.visible = false;
    // Material: the troika Text exposes a single MeshBasicMaterial-like
    // material under .material. We make it transparent so opacity-fade works.
    if (t.material) {
      (t.material as THREE.Material).transparent = true;
      (t.material as THREE.Material & { toneMapped?: boolean }).toneMapped = false;
    }
    group.add(t);
    return { text: t as unknown as PoolEntry["text"], boundId: null };
  }

  function ensurePoolSize(n: number) {
    while (pool.length < n) pool.push(makeText());
  }

  function syncNodes(nodes: TextLayerNode[]) {
    nodeCache.clear();
    for (const n of nodes) {
      nodeCache.set(n.id, { title: n.title, tint: n.tint });
    }
    // Park any pool entry whose bound id no longer exists.
    for (const entry of pool) {
      if (entry.boundId && !nodeCache.has(entry.boundId)) {
        entry.boundId = null;
        const t = entry.text as unknown as { visible: boolean };
        t.visible = false;
      }
    }
    // boundIdx may carry stale ids — drop them.
    for (const id of [...boundIdx.keys()]) {
      if (!nodeCache.has(id)) boundIdx.delete(id);
    }
  }

  // Reused scratch — avoid per-frame allocations.
  const tmpCamPos = new THREE.Vector3();
  // Score buffer: { idx, dist² }. Reused across frames.
  let scoreBuf: Array<{ id: string; idx: number; d2: number }> = [];

  function updateTextLayer(camera: THREE.Camera, nodes: TextLayerNode[]) {
    if (nodes.length === 0) {
      // Hide everything.
      for (const e of pool) {
        if (e.boundId) {
          (e.text as unknown as { visible: boolean }).visible = false;
          e.boundId = null;
        }
      }
      return;
    }
    camera.getWorldPosition(tmpCamPos);
    const FADE_FAR_2 = FADE_FAR * FADE_FAR;

    // 1. Compute distance² for every node, drop those past FADE_FAR.
    if (scoreBuf.length !== nodes.length) {
      scoreBuf = new Array(nodes.length);
    }
    let inRangeCount = 0;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i]!;
      const dx = n.pos.x - tmpCamPos.x;
      const dy = n.pos.y - tmpCamPos.y;
      const dz = n.pos.z - tmpCamPos.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 > FADE_FAR_2) continue;
      scoreBuf[inRangeCount++] = { id: n.id, idx: i, d2 };
    }

    // 2. Partial-sort by distance — Array#sort is fine at ≤4096 entries.
    const sliced = scoreBuf.slice(0, inRangeCount);
    sliced.sort((a, b) => a.d2 - b.d2);
    const top = sliced.slice(0, MAX_VISIBLE_LABELS);

    // 3. Build a "wanted" id-set. Park pool entries whose id isn't wanted.
    const wanted = new Set<string>();
    for (const t of top) wanted.add(t.id);
    for (const e of pool) {
      if (e.boundId && !wanted.has(e.boundId)) {
        e.boundId = null;
        (e.text as unknown as { visible: boolean }).visible = false;
      }
    }

    // 4. Bind wanted ids to free pool entries (or grow the pool).
    ensurePoolSize(top.length);
    for (const t of top) {
      let slotIdx = boundIdx.get(t.id);
      let entry = slotIdx !== undefined ? pool[slotIdx] : undefined;
      if (!entry || entry.boundId !== t.id) {
        // Find a parked slot.
        let foundIdx = -1;
        for (let p = 0; p < pool.length; p++) {
          if (pool[p]!.boundId === null) { foundIdx = p; break; }
        }
        if (foundIdx === -1) continue; // pool exhausted (shouldn't happen post-ensurePoolSize)
        entry = pool[foundIdx]!;
        slotIdx = foundIdx;
        const meta = nodeCache.get(t.id);
        if (!meta) continue;
        const txt = entry.text as unknown as { text: string; color: number | string; sync: (cb?: () => void) => void };
        txt.text = meta.title;
        txt.color = TINT_HEX[meta.tint] ?? TINT_HEX.violet;
        txt.sync();
        entry.boundId = t.id;
        boundIdx.set(t.id, slotIdx);
      }
      // Update position + opacity each frame (sim still moves the node).
      const node = nodes[t.idx]!;
      const tObj = entry.text as unknown as {
        visible: boolean;
        position: THREE.Vector3;
        quaternion: THREE.Quaternion;
        material: THREE.Material & { opacity?: number };
        fillOpacity?: number;
      };
      tObj.visible = true;
      tObj.position.set(node.pos.x, node.pos.y + PANEL_TOP_OFFSET, node.pos.z);
      // Billboard toward camera — copy camera quaternion onto the text mesh
      // so it always faces us. (troika respects the mesh's quaternion.)
      tObj.quaternion.copy((camera as THREE.PerspectiveCamera).quaternion);
      // Opacity ramp: 1.0 inside FADE_NEAR, 0.0 at FADE_FAR.
      const dist = Math.sqrt(t.d2);
      const fade = dist <= FADE_NEAR
        ? 1.0
        : Math.max(0, 1 - (dist - FADE_NEAR) / (FADE_FAR - FADE_NEAR));
      // troika respects fillOpacity (preferred) and falls back to material.opacity.
      tObj.fillOpacity = fade;
      if (tObj.material && tObj.material.opacity !== undefined) {
        tObj.material.opacity = fade;
      }
    }
  }

  function dispose() {
    for (const e of pool) {
      const t = e.text as unknown as { dispose: () => void };
      try { t.dispose(); } catch { /* ok */ }
    }
    pool.length = 0;
    boundIdx.clear();
    nodeCache.clear();
    if (group.parent) group.parent.remove(group);
  }

  return { group, syncNodes, updateTextLayer, dispose };
}
